

// MODULES
import { mapGetters, mapActions } from 'vuex';

// API
import * as apiPlayground from '../api/public/playground';
import * as apiCommentPrivate from '../api/private/comment';
import * as apiComment from '../api/public/comment';
import * as apiContributions from '../api/private/contributions';

import * as apiFavorite from '../api/private/favorite';

// MIXINS
import errorsMixin from '../mixins/error-handler';
import metaMixin from '../mixins/meta';

// LIBRARIES
import { getDisplayPlaceName, getPlaceNameWithPreposition } from '../libraries/utils';

// ASYNC COMPONENT
import SLoadingState from '../components/SLoadingState.vue';
import SErrorState from '../components/SErrorState.vue';

const SResultListViewLazy = () => ({
  component: import('../components/SResultListView.vue'),
  loading: SLoadingState,
  error: SErrorState,
  timeout: 60000,
});

const SContextualListLazy = () => ({
  component: import('../components/SContextualList.vue'),
  loading: SLoadingState,
  error: SErrorState,
  timeout: 60000,
});

export default {
  name: 'Detail',
  components: {
    SResultListViewLazy,
    SContextualListLazy,
  },
  mixins: [errorsMixin, metaMixin],
  beforeRouteLeave(to, from, next) {
    if (!this.unSaveContribution && !this.statusCommentFormApiCall) {
      next();
      return;
    }

    if (this.statusCommentFormApiCall) {
      this.ongoingApiCall = true;
    } else if (this.unSaveContribution) {
      this.showAlert = true;
    }
    // AppNav => navigation via app ou navigateur
    if (!to.params?.appNav) {
      const title = document.title;
      document.title = '';
      document.title = title;
      this.$newRouter.pushState({ ...from.params }, from.name, from.path);
    }
    this.cancelNav = () => {
      this.resetNavFunc();
    };
    this.confirmNav = () => {
      this.resetNavFunc();
      next();
    };
  },
  props: {
    id: {
      type: Number,
      default: null,
    },
    listPlace: {
      type: String,
      default: null,
    },
    listLat: {
      type: Number,
      default: null,
    },
    listLon: {
      type: Number,
      default: null,
    },
    userLocation: {
      type: Boolean,
      default: false,
    },
  },
  async asyncData({ params, error }) {
    let playground = null;
    let serverTime = null;

    const listComments = [];
    const listContributors = [];
    const defaultErrors$ = [];
    const asyncErrors$ = [];

    // Liste API async
    const apiCalls = [];

    let id = Number(params.id);
    if (Number.isNaN(id)) {
      id = null;
    }
    if (process.server) {
      if (id) {
        apiCalls.push(apiPlayground.list({ ids: [id] }, null).then((res) => {
          playground = res.playgrounds[0] ?? null;
          serverTime = res.time;
        // Empty catch, if there is no result, there's a redirect to 404 page
        }).catch(() => null));

        const listCommentsData = {
          playground: id,
          size: 5,
          page: 1,
        };
        apiCalls.push(apiComment.listComments(listCommentsData, null).then((comment) => {
          listComments.push({ id, ...comment });
        }).catch((e) => {
          if (e?.response?.data) {
            asyncErrors$.push(e.response.data);
          }
          defaultErrors$.push({ env: 'default_comment_list', field: 'form' });
        }));

        apiCalls.push(apiPlayground.listContributors(id, null).then((contributor) => {
          listContributors.push({ id, ...contributor });
        }).catch((e) => {
          if (e?.response?.data) {
            asyncErrors$.push(e.response.data);
          }
          defaultErrors$.push({ env: 'default_contributors', field: 'form' });
        }));

        // Attendre toute les API
        await Promise.allSettled(apiCalls);
      }
      if (!playground) {
        error({ statusCode: 404 });
      }
    }

    return {
      playground, listComments, listContributors, asyncErrors$, defaultErrors$, serverTime,
    };
  },
  data() {
    return {
      // BUG HOT RELOAD
      playground: null,
      serverTime: null,
      listComments: [],
      listContributors: [],
      // KEEP-ALIVE
      isComponentActive: false,
      keepMobileLayout: null,
      // CONTENT
      contextTitle: '',
      contextGeo: false,
      listHasComments: [],
      pendingCommentPublic: false,
      listHasContributions: [],
      listContributions: [],
      // PUB
      pubSpace: 0,
      // NAVIGATION
      unSaveContribution: false,
      confirmNav: () => {},
      cancelNav: () => {},
      // DIALOG
      showAlert: false,
      alertTitle: 'Saisie en cours',
      alertButtons: {
        closeText: 'Annuler',
        confirmText: 'OK',
      },
      // ONGOING API CALL
      statusCommentFormApiCall: null,
      ongoingApiCall: false,
      waitEditContribution: false,
      // ERROR
      contextualMobileError: false,
      contextualDesktopError: false,
      favoriteHasError: false,
    };
  },
  head() {
    const head = {};

    if (this.playground) {
      head.title = this.metaTitle;

      const metas = [
        {
          hid: 'title',
          name: 'title',
          content: this.metaTitle,
        },
        {
          hid: 'description',
          name: 'description',
          content: this.playground.description || this.metaDescription,
        },
      ];

      if (this.playground.pictures.length > 0) {
        metas.push({
          hid: 'image',
          name: 'image',
          content: `${process.env.VUE_APP_ASSETS_URL}playground/lg/${this.playground.pictures[0]}.jpg`,
        });
      }

      head.meta = this.meta(metas);
    }

    return head;
  },
  computed: {
    ...mapGetters({
      getCurrentSearch: 'transferData/getCurrentSearch',
      getContext: 'transferData/getContext',
      getContextComments: 'transferData/getContextComments',
      getContextContributions: 'transferData/getContextContributions',
      getContextualPlaygrounds: 'transferData/getContextualPlaygrounds',
      getResults: 'transferData/getResults',
      getCurrentDate: 'transferData/getCurrentDate',
      getWindowWidth: 'screen/getWindowWidth',
      isAuth: 'authToken/isAuth',
      userSecured: 'preferences/userSecured',
      isHGte: 'screen/isHGte',
      getUserControl: 'syncTab/getUserControl',
    }),

    // DISPLAY -----------------------------------------------------------------------------------------
    mobileLayout() {
      if (this.isComponentActive || this.keepMobileLayout === null) {
        if (this.getWindowWidth >= (320 + 490 + 160 + 5 * 2)) {
          this.enableToolbar();
          return false;
        }
        if (this.contextualMobileError) {
          this.enableToolbar();
        } else {
          this.disableToolbar();
        }
        return true;
      }
      return this.keepMobileLayout;
    },

    // LIST --------------------------------------------------------------------------------------------
    contextDetail() {
      // Context via props (avoid wrong context with two tabs)
      if (this.listPlace || this.listLat || this.listLon) {
        return {
          place: this.listPlace,
          lat: this.listLat,
          lon: this.listLon,
          geoSearch: !!this.listLat && !!this.listLon,
          userLocation: this.userLocation,
        };
      }

      // Check store (keep context on reload or if play in result)
      if (this.getCurrentSearch && this.getCurrentDate && this.getResults?.some((result) => result.id === this.id)) {
        return {
          ...this.getCurrentSearch,
          store: true,
        };
      }

      // Take current play context (direct access with no context, use URL params)
      return null;
    },
    displayList() {
      // Display the contextual list if it is an error returned by it in desktop mode
      if (this.contextualDesktopError) {
        return false;
      }
      if (this.getContext !== 'list') {
        // History forward from a contextual page (favorites, comments)
        // to a detail that is no longer in the contextual list
        // will return true and display the normal list, not the contextual list.
        return !this.getContextualPlaygrounds?.find((result) => result.id === this.id);
      }
      return this.getContext === 'list';
    },

    // DETAIL ------------------------------------------------------------------------------------------
    playgroundAdminAlias() {
      if (this.playground) {
        return this.playground.admin ? this.playground.admin.alias : this.playground.country.alias;
      }
      return null;
    },
    playgroundPlaceAlias() {
      if (this.playground) {
        return this.playground.city.alias;
      }
      return null;
    },
    playgroundPlaceName() {
      if (this.playground) {
        return getDisplayPlaceName(this.playground.city, this.playground.sub);
      }
      return null;
    },

    // SWIPER ------------------------------------------------------------------------------------------

    swiperIndex() {
      return Math.max(this.listPlay.findIndex((element) => element?.id === this.id), 0);
    },

    listPlay() {
      if (this.mobileLayout) {
        if (this.displayList) {
          if (this.getResults?.find((result) => result.id === this.id)) {
            return this.getResults;
          }
          this.updateSearchResult([]);
        } else if (this.getContextualPlaygrounds?.find((result) => result.id === this.id)) {
          return this.getContextualPlaygrounds;
        }
      }
      return this.playground ? [this.playground] : [];
    },

    // META --------------------------------------------------------------------------------------------
    metaTitle() {
      if (this.playgroundPlaceName) {
        const placeNameWithPreposition = getPlaceNameWithPreposition(this.playgroundPlaceName);
        return `Aire de jeux ${placeNameWithPreposition}, ${this.playground.street}`;
      }
      return null;
    },
    metaDescription() {
      if (this.playgroundPlaceName) {
        const placeNameWithPreposition = getPlaceNameWithPreposition(this.playgroundPlaceName);
        return `Toutes les informations sur l'aire de jeux ${placeNameWithPreposition}, ${this.playground.street}`;
      }
      return undefined;
    },
    breadcrumbMicrodatas() {
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [{
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil | ADJ',
          item: `${process.env.VUE_APP_URL}`,
        }, {
          '@type': 'ListItem',
          position: 2,
          name: 'Recherche une aire de jeux | ADJ',
          item: `${process.env.VUE_APP_URL}search`,
        }, {
          '@type': 'ListItem',
          position: 3,
          name: `${this.metaTitle} | ADJ`,
          item: `${process.env.VUE_APP_URL}${this.playgroundAdminAlias}/${this.playgroundPlaceAlias}/${this.playground.id}`,
        }],
      };
    },
  },
  watch: {
    // LOAD CONTENT ------------------------------------------------------------------------------------
    id: {
      immediate: true,
      async handler() {
        if (Object.keys(this.errors$).length) {
          this.errors$.reset();
        }
        await this.initPlayground();
        this.getCommentsApi();
        this.getCommentPublic();
        this.getHasContributions();
        this.getContributorsApi();
      },
    },

    // CONNECTION --------------------------------------------------------------------------------------
    isAuth: {
      immediate: true,
      handler(value) {
        this.listHasComments = [];
        this.getFavorites();
        this.getCommentPublic();

        // Reset unsaveContribution if user is logged out
        if (!value) {
          this.unSaveContribution = false;
        }
      },
    },

    userSecured() {
      if (!this.getUserControl.active) {
        this.listHasContributions = [];
        this.listContributions = [];
        this.getHasContributions();
      }
    },

    // ONGOING API CALL --------------------------------------------------------------------------------
    statusCommentFormApiCall() {
      if (this.statusCommentFormApiCall === 'SUCCESS') {
        this.confirmNav();
        this.statusCommentFormApiCall = null;
      } else if (this.statusCommentFormApiCall === 'ERROR') {
        this.cancelNav();
        this.statusCommentFormApiCall = null;
      }
    },
  },
  created() {
    if (process.client) {
      // Get space around pub for dynamic layout
      const pubSpaceCss = getComputedStyle(document.documentElement).getPropertyValue('--pub-space');
      this.pubSpace = parseInt(pubSpaceCss.replace('px', ''), 10);

      if (this.serverTime) {
        this.updateCurrentDate(this.serverTime);
        if (this.mobileLayout) {
          this.updateSearchResult([]);
        }
      }
    }

    // Direct access with the short url
    if (this.$route.name === 'detail' && process.client && this.playground) {
      this.$newRouter.replace({
        name: 'detail-list',
        params: {
          admin: this.playgroundAdminAlias,
          place: this.playgroundPlaceAlias,
          id: this.id,
        },
      });
    } else {
      this.rewriteUrl();
    }
  },
  activated() {
    this.keepMobileLayout = null;
    this.isComponentActive = true;
  },
  deactivated() {
    this.keepMobileLayout = this.mobileLayout;
    this.isComponentActive = false;
  },
  // STOP NAVIGATION ---------------------------------------------------------------------------------
  beforeMount() {
    // STOP RELOAD -------------------------------------------------------------------------------
    const onBeforeUnload = (event) => {
      if (this.unSaveContribution) {
        event.returnValue = '';
      }
      return null;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    });
  },
  methods: {
    ...mapActions({
      disableSideMenu: 'menu/disableSideMenu',
      disableToolbar: 'menu/disableToolbar',
      enableSideMenu: 'menu/enableSideMenu',
      enableToolbar: 'menu/enableToolbar',
      setListFavorites: 'transferData/setListFavorites',
      resetContext: 'transferData/resetContext',
      updateContextComments: 'transferData/updateContextComments',
      updateContextContributions: 'transferData/updateContextContributions',
      updateContextualPlaygrounds: 'transferData/updateContextualPlaygrounds',
      updateCurrentDate: 'transferData/updateCurrentDate',
      updateSearchResult: 'transferData/updateSearchResult',
    }),

    rewriteUrl() {
      if (process.client && this.playground) {
        const { admin, place } = this.$route.params;
        if (this.playgroundPlaceAlias && (admin !== this.playgroundAdminAlias || place !== this.playgroundPlaceAlias)) {
          this.$newRouter.replace({
            name: 'detail-list',
            params: {
              admin: this.playgroundAdminAlias,
              place: this.playgroundPlaceAlias,
              id: this.id,
            },
          });
        }
      }
    },

    initContext(data) {
      this.contextTitle = data.contextTitle;
      this.contextGeo = data.contextGeo;
    },

    async initPlayground() {
      if (this.playground?.id !== this.id) {
        if (this.getContext !== 'list') {
          this.playground = this.getContextualPlaygrounds?.find((play) => play.id === this.id);
          if (this.playground) return;
        }
        this.playground = this.getResults?.find((play) => play.id === this.id);
        if (this.playground) return;

        // Empty catch, if there is no result, there's a redirect to 404 page
        if (this.id) {
          const result = await apiPlayground.list({ ids: [this.id] }, this).catch(() => null);
          if (result) {
            this.updateCurrentDate(result.time);
            this.playground = result.playgrounds[0] ?? null;
          }
          if (this.playground) return;
        }
        if (process.client) {
          this.$newRouter.replace({
            name: '404',
            params: { url: this.$route.path },
          });
        }
      }
    },
    getCommentsApi() {
      const playIndex = this.listComments.findIndex((play) => play.id === this.id);
      if (this.playground && playIndex === -1) {
        apiComment.listComments({
          playground: this.id,
          size: 5,
          page: 1,
        }, this).then((list) => {
          this.listComments.push({
            id: this.id,
            ...list,
          });
        }).catch((error) => {
          // Validation error
          if (error.response && error.response.status === 404) {
            this.errors$.handleError(true, 'default_comment_list', 'form');
          }
        });
      }
    },
    getContributorsApi() {
      const playIndex = this.listContributors.findIndex((play) => play.id === this.id);
      if (playIndex === -1 && this.id) {
        apiPlayground.listContributors(this.id, this).then((list) => {
          this.listContributors.push({
            id: this.id,
            ...list,
          });
        }).catch((error) => {
          // Validation error
          if (error.response && error.response.status === 404) {
            this.errors$.handleError(true, 'default_contributors', 'form', null);
          }
        });
      }
    },
    getCommentPublic() {
      if (!this.pendingCommentPublic) {
        const playIndex = this.listHasComments.findIndex((play) => play.id === this.id);
        if (this.isAuth && playIndex === -1 && this.id) {
          this.pendingCommentPublic = true;
          apiCommentPrivate.commentPublic(this.id, this).then((res) => {
            this.listHasComments.push({
              id: this.id,
              hasComment: !!res.has_comment,
            });
          }).catch(() => {
            this.errors$.handleError(true, 'default_comment_list', 'form');
          }).finally(() => {
            this.pendingCommentPublic = false;
          });
        }
      }
    },
    getHasContributions() {
      const playIndex = this.listHasContributions.findIndex((play) => play.id === this.id);
      if (this.userSecured && playIndex === -1 && this.id) {
        this.ongoingApiCall = true;
        apiContributions.hasContributions(this.id, this).then((contributions) => {
          this.listHasContributions.push({
            id: this.id,
            contributions,
          });
        // Empty catch, because we don't want to display an error message. Non-blocking feature.
        }).catch(() => null).finally(() => {
          this.ongoingApiCall = false;
        });
      }
    },
    getContributions() {
      const playIndex = this.listContributions.findIndex((play) => play.id === this.id);
      if (this.userSecured && playIndex === -1 && this.id) {
        this.waitEditContribution = true;
        apiContributions.contributionsById(this.id, { type: 'playground' }, this).then((contributions) => {
          this.listContributions.push({
            id: this.id,
            street: contributions.street,
            description: contributions.description,
            facilities: contributions.facilities,
            location: {
              lat: contributions.lat,
              lon: contributions.lon,
            },
          });
        }).catch(() => {
          // Return a 404 error when there is no contribution
          // If there is an error, set the default data as if there were no contribution
          this.listContributions.push({
            id: this.id,
            street: null,
            description: null,
            facilities: null,
            location: {
              lat: null,
              lon: null,
            },
          });
        }).finally(() => {
          this.waitEditContribution = false;
        });
      }
    },

    getFavorites() {
      if (this.isAuth) {
        apiFavorite.listFavorites(this).then((list) => {
          const favIds = list.results.map((result) => result.playground);
          this.setListFavorites(favIds);
        // catch null => favorite error is not blocking
        // => disable button & error on tooltip
        }).catch(() => {
          this.favoriteHasError = true;
        });
      }
    },

    // COMMENT ------------------------------------------------------------------------------------
    updateDetailData(data) {
      if (data.listComments) {
        const commentIndex = this.listComments.findIndex((play) => play.id === data.listComments.id);
        this.listComments[commentIndex] = data.listComments;
      }
      if (data.listHasComments) {
        const hasCommentIndex = this.listHasComments.findIndex((play) => play.id === data.listHasComments.id);
        if (hasCommentIndex >= 0) {
          this.listHasComments[hasCommentIndex] = data.listHasComments;
        } else {
          this.listHasComments.push(data.listHasComments);
        }
      }
      if (data.listContributions) {
        const contributionsIndex = this.listContributions.findIndex((play) => play.id === data.listContributions.id);
        this.listContributions[contributionsIndex] = data.listContributions;
      }
      if (data.listHasContributions) {
        const hasContributionsIndex = this.listHasContributions.findIndex((play) => play.id === data.listHasContributions.id);
        if (hasContributionsIndex >= 0) {
          this.listHasContributions[hasContributionsIndex] = data.listHasContributions;
        } else {
          this.listHasContributions.push(data.listHasContributions);
        }
      }
      this.initPlayground();
    },

    // SWIPER ------------------------------------------------------------------------------------------
    onSlide(index) {
      const swipeElement = this.listPlay[index];
      // Change the url if we are already on a detail, not if the detail is in keep-alive
      if (this.$route.name === 'detail-list') {
        const newDetailId = swipeElement.id;
        const newDetailAdmin = swipeElement.admin ? swipeElement.admin.alias : swipeElement.country.alias;
        const newDetailPlace = swipeElement.city.alias;
        this.$newRouter.replace({ name: 'detail-list', params: { admin: newDetailAdmin, place: newDetailPlace, id: newDetailId } });
      }

      if (!this.displayList && index === this.listPlay.length - 2) {
        if (this.getContext === 'comment' && !this.getContextComments?.end) {
          this.loadMoreComment();
        } else if (this.getContext === 'contributions' && !this.getContextContributions?.end) {
          this.loadMoreContributions();
        }
      }
    },

    // NAVIGATION ---------------------------------------------------------------------------------
    loadDetail(playground) {
      const newDetailId = playground.id;
      const newDetailAdmin = playground.admin ? playground.admin.alias : playground.country.alias;
      const newDetailPlace = playground.city.alias;
      if (!this.unSaveContribution && !this.statusCommentFormApiCall) {
        this.$newRouter.replace({ name: 'detail-list', params: { admin: newDetailAdmin, place: newDetailPlace, id: newDetailId } });
        return;
      }

      if (this.statusCommentFormApiCall) {
        this.ongoingApiCall = true;
      } else if (this.unSaveContribution) {
        this.showAlert = true;
      }
      this.cancelNav = () => {
        this.resetNavFunc();
      };
      this.confirmNav = () => {
        this.$newRouter.replace({ name: 'detail-list', params: { admin: newDetailAdmin, place: newDetailPlace, id: newDetailId } });
        this.resetNavFunc();
      };
    },
    resetNavFunc() {
      this.showAlert = false;
      this.ongoingApiCall = false;
      this.cancelNav = () => {};
      this.confirmNav = () => {};
      this.$newRouter.cancel();
    },
    onCancel(data) {
      if (this.statusCommentFormApiCall) {
        this.ongoingApiCall = true;
      } else if (this.unSaveContribution) {
        this.showAlert = true;
      }
      this.cancelNav = () => {
        this.resetNavFunc();
      };
      this.confirmNav = () => {
        if (data.previous) {
          this.$refs.swiper.$swiper.slidePrev();
        } else if (data.next) {
          this.$refs.swiper.$swiper.slideNext();
        }
        this.resetNavFunc();
      };
    },

    // CONTEXTUAL COMMENT ------------------------------------------------------------------------------
    loadMoreComment() {
      const page = this.getContextComments.page + 1;
      const size = this.getContextComments.size;
      apiCommentPrivate.commentsUser({ size, page }, this).then(async (list) => {
        const ids = list.results.map((comment) => comment.playground_id);
        await this.loadMorePlaygrounds(ids);
        const listComments = [...this.getContextComments.comments];
        listComments.push(...list.results);
        this.updateContextComments({
          page,
          size,
          end: !list.more,
          comments: listComments,
        });
      }).catch((error) => {
        this.contextualMobileError = true;
        this.enableToolbar();
        this.resetContext();
        // Validation error
        if (error.response && error.response.status === 400) {
          if (this.errors$.hasErrors()) {
            return;
          }
          this.errors$.handleError(true, 'comment_user_list', 'form', null);
        }
      });
    },
    async loadMoreContributions() {
      const { contributions, offset, size } = this.getContextContributions;
      const ids = contributions.slice(offset, offset + size).map((result) => result.playground);
      await this.loadMorePlaygrounds(ids, contributions);
      if (!this.contextualMobileError) {
        const newOffset = offset + size;
        this.updateContextContributions({
          contributions,
          offset: newOffset,
          size,
          end: newOffset >= contributions.length,
        });
      }
    },
    async loadMorePlaygrounds(ids, contributions) {
      await apiPlayground.list({ ids }, this).then((res) => {
        let newPlaygrounds = res.playgrounds.sort((a, b) => (ids.indexOf(a.id) - ids.indexOf(b.id)));
        newPlaygrounds.unshift(...this.listPlay);
        if (this.getContext === 'contributions') {
          newPlaygrounds = newPlaygrounds.map((playground) => ({
            ...playground,
            contributor: contributions.find((contribution) => contribution.playground === playground.id),
          }));
        }
        this.updateContextualPlaygrounds([...newPlaygrounds]);
      }).catch((error) => {
        this.contextualMobileError = true;
        this.enableToolbar();
        this.resetContext();
        // Validation error
        if (error.response && error.response.status === 400) {
          if (this.errors$.hasErrors()) {
            return;
          }
          this.errors$.handleError(true, 'playground_search', 'form', null);
        }
      });
    },
  },
};
